/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useMemo } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import CodeBlock from '@atlaskit/code/code-block';
import { cssMap, jsx } from '@atlaskit/css';
import VideoPlayOverlayIcon from '@atlaskit/icon/core/video-play-overlay';
import { Box, Text, Stack } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner/spinner';
import { token } from '@atlaskit/tokens';

import { type MessageRole } from '../../../../../../../contexts/AsideContext';
import { messages } from './messages';
import { parseContent, renderTextWithBold } from './utils';

const styles = cssMap({
	messageContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.050'),
	},
	userMessage: {
		alignSelf: 'flex-end',
		backgroundColor: token('color.background.selected'),
		borderRadius: token('radius.medium'),
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
		maxWidth: '85%',
		whiteSpace: 'pre-wrap',
	},
	assistantMessage: {
		alignSelf: 'flex-start',
		backgroundColor: token('color.background.neutral'),
		borderRadius: token('radius.medium'),
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
		maxWidth: '85%',
		whiteSpace: 'pre-wrap',
	},
	loadingMessage: {
		alignSelf: 'flex-start',
		backgroundColor: token('color.background.neutral'),
		borderRadius: token('radius.medium'),
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
	},
	codeBlockWrapper: {
		marginBlockStart: token('space.100'),
		marginBlockEnd: token('space.100'),
	},
	actionRow: {
		display: 'flex',
		flexDirection: 'row',
		flexWrap: 'wrap',
		justifyContent: 'flex-start',
		marginBlockStart: token('space.100'),
		gap: token('space.100'),
	},
	actionButton: {
		flex: '0 1 auto',
		minWidth: '140px',
	},
});

const CypherCodeBlock = ({ content }: { language: string; content: string }): JSX.Element => {
	const codeLanguage = 'plaintext' as const;

	return (
		<Box xcss={styles.codeBlockWrapper}>
			<CodeBlock text={content} language={codeLanguage} shouldShowLineNumbers={false} />
		</Box>
	);
};

export type ChatMessageViewProps = {
	messageRole: MessageRole;
	content: string;
	isLoading?: boolean;
	rawCypherQuery?: string;
	assistantName?: string;
	onUseCypher?: () => void;
};

export const ChatMessageView = ({
	messageRole,
	content,
	isLoading = false,
	rawCypherQuery,
	assistantName,
	onUseCypher,
}: ChatMessageViewProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const isUser = messageRole === 'user';

	const resolvedAssistantName = assistantName ?? formatMessage(messages.defaultAssistantName);
	const parsedContent = useMemo(() => parseContent(content), [content]);

	if (isLoading) {
		return (
			<Box xcss={styles.messageContainer}>
				<Text size="small" color="color.text.subtlest">
					{resolvedAssistantName}
				</Text>
				<Box xcss={styles.loadingMessage}>
					<Spinner size="small" />
					<Text color="color.text.subtlest">{content}</Text>
				</Box>
			</Box>
		);
	}

	return (
		<Box xcss={styles.messageContainer}>
			<Text size="small" color="color.text.subtlest">
				{isUser ? <FormattedMessage {...messages.youLabel} /> : resolvedAssistantName}
			</Text>
			<Box xcss={isUser ? styles.userMessage : styles.assistantMessage}>
				<Stack space="space.050">
					{parsedContent.map((part, index) => {
						if (part.type === 'code') {
							return (
								<CypherCodeBlock key={index} language={part.language} content={part.content} />
							);
						}
						return <Text key={index}>{renderTextWithBold(part.content)}</Text>;
					})}
					{rawCypherQuery && onUseCypher && (
						<Box xcss={styles.actionRow}>
							<Box xcss={styles.actionButton}>
								<Button
									appearance="primary"
									onClick={onUseCypher}
									iconBefore={(iconProps) => <VideoPlayOverlayIcon {...iconProps} />}
								>
									<FormattedMessage {...messages.useCypherButton} />
								</Button>
							</Box>
						</Box>
					)}
				</Stack>
			</Box>
		</Box>
	);
};
